import { ServerApi } from './server-api';
import axios from 'axios';
import { InviteDTO } from './server-types';
import { addInteractionMonitoring } from './fedops';
import { PolicyDTO } from '../types/roles';

export const INVITES_ENDPOINT = '/_api/invites-web/invites/account';

export default (): ServerApi => {
  const axiosInstance = axios.create();

  const fetchTeamMembers = addInteractionMonitoring((usersLimit?) => {
    let fetchTeamMembersEndpoint = `/_api/roles-management-web/team`;
    if (usersLimit) {
      fetchTeamMembersEndpoint += `?usersLimit=${usersLimit}`;
    }
    return axiosInstance
      .get(fetchTeamMembersEndpoint)
      .then(res => res.data);
  }, 'account-fetch-team-members');

  const fetchTeamMemberUsers = addInteractionMonitoring((limit: number, pageNum: number) => {
    return axiosInstance
      .get(`/_api/roles-management-web/team/users?limit=${limit}&offset=${pageNum}`)
      .then(res => res.data);
  }, 'account-fetch-team-member-users');

  const inviteContributorsWithPolicyIds = addInteractionMonitoring((emails: string[], policyIds: string[], accountId: string) => {
    return axiosInstance
      .post(`${INVITES_ENDPOINT}/bulk`, { accountId, policyIds, emails })
      .then(res => res.data);
  }, 'account-invite-contributors');

  const removeContributor = addInteractionMonitoring((userId: string) => {
    return axiosInstance
      .post('/_api/roles-management-web/team/remove', { userId })
      .then(res => res.data);
  }, 'account-remove-contributor');

  const revokeInvite = addInteractionMonitoring((inviteId: string) => {
    return axiosInstance
      .post(`${INVITES_ENDPOINT}/${inviteId}/revoke`)
      .then(res => res.data);
  }, 'account-revoke-invite');

  const resendInvite = addInteractionMonitoring((inviteId: string): Promise<InviteDTO> => {
    return axiosInstance
      .post(`${INVITES_ENDPOINT}/${inviteId}/resend`)
      .then(res => res.data.invite);
  }, 'account-resend-invite');

  const setTeamDetails = addInteractionMonitoring((accountId: string, accountName: string, accountImg: string) => {
    return axiosInstance
      .patch(`/_api/account-server/v1/accounts/${accountId}`, {
        accountId,
        accountName,
        accountImg: !!accountImg ? accountImg : '',
      })
      .then(res => res.data);
  }, 'account-set-team-details');

  const setInviteDetails = addInteractionMonitoring((inviteId: string, policyIds: string[]) => {
    const setInviteDetailsParam = { policyIds };
    return axiosInstance
      .patch(`${INVITES_ENDPOINT}/${inviteId}`, setInviteDetailsParam)
      .then(res => res.data);
  }, 'account-set-invite-details');

  const setContributorDetails = addInteractionMonitoring((userId: string, policyIds: string[]) => {
    const params =  { policyIds };
    return axiosInstance
      .post(`/_api/roles-management-web/team/${userId}/change`, {
        ...params,
        userId,
      })
      .then(res => res.data);
  }, 'account-set-contributor-details');

  // TODO: refactor this method. Maybe AccMgr/BizMgr have the data in the framework already
  const fetchCurrentUserDetails = addInteractionMonitoring(async () => {
    const [{ profileImage }, { first, last }] = (await Promise.all([
      axiosInstance.get(`/_api/business-manager/user`),
      axiosInstance.get(`/_api/my-account-services/user`),
    ])).map(res => res.data);

    return { first, last, profileImage };
  }, 'account-fetch-current-user-details');

  const createRole = addInteractionMonitoring(async (policy: PolicyDTO) => {
    return axiosInstance
        .post('/_api/authorization-server/v1/policies', { policy })
        .then(res => res.data);
  }, 'account-set-role');

  const updateRoleStatements = addInteractionMonitoring(async ({ id, statements }: Pick<PolicyDTO, 'id' | 'statements'>) => {
    return axiosInstance
      .put(`/_api/authorization-server/v1/policies/${id}/authorization-data`, { policyStatements: statements })
      .then(res => res.data);
  }, 'account-update-role-statements');

  const updateRoleMeta = addInteractionMonitoring(async (policy: Omit<PolicyDTO, 'statements'>) => {
    return axiosInstance
      .patch(`/_api/authorization-server/v1/policies/${policy.id}/metadata`, policy)
      .then(res => res.data);
  }, 'account-update-role-meta');

  const removeRole = addInteractionMonitoring(async (policyId: string) => {
    return axiosInstance
        .delete(`/_api/authorization-server/v1/policies/${policyId}`)
        .then(res => res.data);
  }, 'account-remove-role');

  const isAppInstalled = () => {
    throw 'not implemented';
  };

  const resendTransferOwnershipEmail = () => {
    throw 'not implemented';
  };

  const cancelTransferOwnership = () => {
    throw 'not implemented';
  };

  const fetchContributorLimit = () => {
    throw 'not implemented';
  };

  return {
    fetchTeamMembers,
    fetchTeamMemberUsers,
    inviteContributorsWithPolicyIds,
    setTeamDetails,
    removeContributor,
    revokeInvite,
    resendInvite,
    setInviteDetails,
    setContributorDetails,
    fetchCurrentUserDetails,
    createRole,
    updateRoleStatements,
    updateRoleMeta,
    removeRole,
    isAppInstalled,
    resendTransferOwnershipEmail,
    cancelTransferOwnership,
    fetchContributorLimit,
  };
};
